import type { Map as MapboxMap } from 'mapbox-gl';
import mapboxgl from 'mapbox-gl';
import { useEffect } from 'react';
import {
  DEAL_RING_CENTER_ABOVE_ANCHOR,
  DEAL_RING_ICON_SIZE,
  DEAL_RING_RADIUS,
} from '@/features/locations/utils/dealMarkerIcon';
import type { Coordinates } from '@/features/map/context/ManualPinContext';

/**
 * The deal's mark is the ring icon, anchored by its bottom tip — so its centre sits well above the
 * coordinate, and the burst has to be lifted to match or it goes off at the tip instead of around
 * the mark. Both are derived from the icon's own geometry so they follow it.
 */
const RING_CENTER_OFFSET = DEAL_RING_CENTER_ABOVE_ANCHOR * DEAL_RING_ICON_SIZE;
const RING_RADIUS = DEAL_RING_RADIUS * DEAL_RING_ICON_SIZE;

/** Just outside the mark's border, where the sparks appear: close enough to read as its own. */
const SPARK_CLEARANCE = 4;

/** How much further out the sparks travel from where they appear. */
const SPARK_TRAVEL = 16;

/**
 * How long the burst plays before it takes itself off the map: three 1.4s rounds plus the last
 * spark's stagger, with a little slack. Mirrors `.deal-burst-spark`.
 */
const BURST_MS = 4600;

/** Sparks, evenly spread. Twelve reads as a burst; more turns to mush at pin size. */
const SPARK_COUNT = 12;

interface DealPromotionBurstProps {
  map: MapboxMap;
  /** Whether the shell's Mapbox style has loaded — a marker added before that is dropped. */
  isMapLoaded: boolean;
  /** The new deal's own point, which is the location's. */
  coordinates: Coordinates;
  /** Called once the burst has played, so the caller stops asking for it. */
  onDone: () => void;
}

/**
 * A one-shot firework around a deal's mark, celebrating the moment a location became a deal.
 *
 * It is a DOM `Marker` rather than anything on the deals layer: the pins are symbol layers drawn
 * on the WebGL canvas, which CSS cannot reach, and a marker also tracks the camera for free if the
 * BD pans while it plays. It removes itself on the timer as well as on unmount, so a card closed
 * mid-burst leaves nothing behind.
 */
export function DealPromotionBurst({
  map,
  isMapLoaded,
  coordinates,
  onDone,
}: DealPromotionBurstProps) {
  useEffect(() => {
    if (!isMapLoaded) return;

    const el = document.createElement('div');
    el.className = 'pointer-events-none relative size-0';
    el.setAttribute('aria-hidden', 'true');

    for (let i = 0; i < SPARK_COUNT; i++) {
      const spark = document.createElement('i');
      spark.className = 'deal-burst-spark';
      // Each spark flies its own way: the angle spreads them evenly, and the radii start them
      // clear of the mark rather than on it. The shared keyframes read all three.
      spark.style.setProperty('--angle', `${(360 / SPARK_COUNT) * i}deg`);
      spark.style.setProperty('--from', `${RING_RADIUS + SPARK_CLEARANCE}px`);
      spark.style.setProperty('--to', `${RING_RADIUS + SPARK_CLEARANCE + SPARK_TRAVEL}px`);
      spark.style.setProperty('--delay', `${(i % 3) * 90}ms`);
      el.appendChild(spark);
    }

    const marker = new mapboxgl.Marker({
      element: el,
      anchor: 'center',
      // Lift the whole burst onto the ring's centre: the coordinate itself is the ring's tip.
      offset: [0, -RING_CENTER_OFFSET],
    })
      .setLngLat([coordinates.longitude, coordinates.latitude])
      .addTo(map);

    const timer = window.setTimeout(onDone, BURST_MS);

    return () => {
      window.clearTimeout(timer);
      marker.remove();
    };
  }, [map, isMapLoaded, coordinates.latitude, coordinates.longitude, onDone]);

  return null;
}
